/* eslint-disable no-nested-ternary */
import { Carousel } from '@components';
import { useScreenSize } from '@hooks';
import { userApi } from '@store';
import { Button, Flex, Image, theme, Typography } from '@styles';
import { arrayOf, bool, func, object } from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useOnlineStatusDetector } from '../../hooks/useOnlineStatusDetector';
import DescriptionCard from '../DescriptionCard/DescriptionCard';
import { Line } from '../Line';
import { Modal } from '../Modal';
import { SparklesAnimation } from '../SparklesAnimation';
import { ActivityCard } from './components';
import { styles } from './RankModuleLayout.styles';

const RankModuleLayout = ({
  isVisible,
  newActivities,
  onClose,
  rank,
  setIsVisible,
}) => {
  const { i18n } = useTranslation();
  const { isOnline } = useOnlineStatusDetector();
  const { data } = userApi.useGetUserQuery(undefined, {
    skip: !isOnline,
  });
  const { isMobile, isTabletSM } = useScreenSize();

  const handleDoneButtonClick = () => {
    onClose?.();
    setIsVisible(false);
  };

  const rankData = rank || data?.data.rank;

  return (
    <Modal
      isVisible={isVisible}
      justifyContent="center"
      modalContainerProps={{
        backgroundColor: theme.colors.white[10],
        borderRadius: '0px',
        maxWidth: isTabletSM ? '100%' : '800px',
        overflow: 'hidden',
      }}
      modalMaxHeight="100%"
      modalMaxWidth={isTabletSM ? '100%' : 800}
      setModalVisible={setIsVisible}
      withBackdropNoise
      withCloseIcon={false}
    >
      <Flex alignItems="center" maxWidth="100vw" zIndex={100}>
        <Flex
          flexDirection="column"
          height="auto"
          justifyContent="space-between"
          maxWidth="100vw"
          px={isTabletSM ? 16 : 40}
          py={40}
        >
          <Flex alignItems="center" flexDirection="column" width="100%">
            <Typography
              color={theme.colors.white[100]}
              mb={24}
              textAlign={isTabletSM ? 'left' : 'center'}
              variant={isTabletSM ? 'BN-bold-s48-lh58' : 'BN-bold-s64-lh64'}
            >
              {i18n.t('Rank.New').toUpperCase()}
            </Typography>
            <Typography
              color={theme.colors.white[100]}
              my={24}
              textAlign={isTabletSM ? 'left' : 'center'}
              textTransform="uppercase"
              variant="MS-bold-s28-lh29"
            >
              {rankData?.name}
            </Typography>
            <Image
              alt="Rank"
              height={45}
              mb={24}
              mixBlendMode="darken"
              src={rankData?.image_large}
              style={{ filter: 'brightness(100)' }}
              width={112}
            />
            <DescriptionCard
              content={
                <>
                  {i18n.t('Rank.ActionDescription.Start')}
                  <b>{i18n.t('Rank.ActionDescription.Finish')}</b>
                  {i18n.t('Rank.ActionDescription.End')}
                </>
              }
              my={32}
            />
          </Flex>
          <Flex flexDirection="column" width="100%">
            {newActivities && (
              <>
                <Flex maxWidth={746} pb={24}>
                  <Carousel
                    containerProps={styles.carouselContainer}
                    items={newActivities || []}
                    renderItem={(item) => (
                      <ActivityCard
                        key={item?.id}
                        id={item?.id}
                        image={item?.image}
                        title={item?.title}
                        type={item?.type}
                      />
                    )}
                    title={
                      <Typography
                        color={theme.colors.white[100]}
                        mb="6px"
                        mt={18}
                        variant="MS-bold-s24-lh30"
                      >
                        {i18n.t('Rank.Unlocked')}
                      </Typography>
                    }
                    withButtons={!isTabletSM}
                  />
                </Flex>
                <Line mb={24} />
              </>
            )}
            <Flex justifyContent="center" width="100%">
              <Button
                maxWidth={isTabletSM ? '100%' : 284}
                onClick={handleDoneButtonClick}
                variant="primary"
                whiteSpace="nowrap"
                width="100%"
              >
                <Typography
                  color={theme.colors.black[100]}
                  variant="MS-semibold-s16-lh25"
                >
                  {i18n.t('Rank.Done').toUpperCase()}
                </Typography>
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
      {isVisible && !isMobile && <SparklesAnimation />}
    </Modal>
  );
};

RankModuleLayout.propTypes = {
  isVisible: bool.isRequired,
  newActivities: arrayOf(object),
  onClose: func,
  rank: object,
  setIsVisible: func.isRequired,
};

RankModuleLayout.defaultProps = {
  newActivities: null,
  onClose: null,
  rank: null,
};

export default RankModuleLayout;
